import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttentionMessage, Box, DataTable, Grid, SimpleGrid, VStack } from '@tq/ui-components';

import { CardLayout, EmptyDataCard } from 'components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { AddressResponse, RelatedApplicationResponse } from 'types';
import { CaseDetailsProps } from '../../types';
import { RelatedApplicationCard } from './components';
import { AddressDetail } from './AddressDetail';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';

export const RelatedCases: React.FC<CaseDetailsProps> = ({ caseDetails }) => {
  const { t } = useTranslation('case_details_related');
  const isMobile = useIsMobile();
  const [applications, setApplications] = useState<RelatedApplicationResponse[]>([]);
  const [addresses, setAddresses] = useState<AddressResponse[]>([]);

  useEffect(() => {
    if (caseDetails?.relatedApplications) {
      setApplications(caseDetails.relatedApplications);
      setAddresses(caseDetails.relatedAddresses);
    }
  }, [caseDetails]);

  return (
    <>
      {isMobile && (
        <AttentionMessage
          mb={6}
          description={t(
            'related_cases_message',
            'The below planning cases will redirect you to the Public Register'
          )}
        />
      )}
      <Grid
        gridAutoFlow={{ base: 'row', lg: 'column' }}
        gridAutoColumns={{ base: 'unset', lg: '49%' }}
        gridGap={6}
      >
        <CardLayout heading={t('related_cases', 'Related cases')}>
          {caseDetails?.relatedApplications?.length ? (
            <>
              {!isMobile && (
                <AttentionMessage
                  description={t(
                    'related_cases_message',
                    'The below planning cases will redirect you to the Public Register'
                  )}
                />
              )}
              <DataTable
                columns={[]}
                data={[]}
                enableInfiniteScroll
                infiniteScrollPageSize={5}
                render={() => (
                  <Grid gridGap={4} mt={{ base: 0, lg: 4 }}>
                    {applications.map(
                      (application: RelatedApplicationResponse, index: number) => {
                        return (
                          <RelatedApplicationCard
                            key={index}
                            relatedCase={application}
                            textExpanded={true}
                          />
                        );
                      }
                    )}
                  </Grid>
                )}
              />
            </>
          ) : (
            <EmptyDataCard
              message={t(
                'no_related_applications_message',
                'No related applications available'
              )}
            />
          )}
        </CardLayout>
        <VStack border={`1px solid ${COLOURS.gray[300]}`} borderRadius={5}>
          <Box
            px={6}
            py={3}
            h={12}
            fontWeight='bold'
            w='100%'
            borderBottom={`1px solid ${COLOURS.gray[300]}`}
          >
            {t('related_addresses', 'Related addresses')}
          </Box>
          <DataTable
            columns={[]}
            data={[]}
            enableInfiniteScroll
            infiniteScrollPageSize={5}
            render={() => (
              <SimpleGrid spacing='0' width='100%'>
                {addresses.map((address: AddressResponse, index: number) => {
                  return (
                    <Box
                      key={index}
                      pb={2}
                      bgColor={index % 2 === 0 ? COLOURS.white : COLOURS.gray[100]}
                    >
                      {address && <AddressDetail relatedAddress={address} />}
                    </Box>
                  );
                })}
              </SimpleGrid>
            )}
          />
          {!addresses?.length && (
            <EmptyDataCard
              message={t('no_related_addresses_message', 'No related addresses available')}
            />
          )}
        </VStack>
      </Grid>
    </>
  );
};
